import QRCode from "qrcode";
import logoQr from "../../asset/gnb/logo_qr.png";
import logoQrTransparent from "../../asset/gnb/logo_qr_transparent.png";
import {
  QR_CANVAS_FONT_FAMILY,
  QR_CANVAS_FONT_WEIGHT,
  drawQRUsernameOnCanvas,
} from "../fonts/pretendardCanvasFont";

export { QR_CANVAS_FONT_FAMILY as QR_USERNAME_FONT_FAMILY };

export interface QRTextOptions {
  text: string;
  position?: 'below-logo' | 'below-qr'; // 로고 밑 또는 QR 코드 밑
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string | number; // 'normal', 'bold', '100', '200', etc.
  color?: string;
  margin?: number; // 텍스트와 위 요소 사이의 간격
}

export type QRTextPosition = 'below-logo' | 'below-qr' | 'none';

const QR_USERNAME_FONT_WEIGHT = QR_CANVAS_FONT_WEIGHT;

/** QR 모달·다운로드 공통 텍스트 옵션 */
export const buildQRGeneratorTextOptions = (
  username: string | undefined,
  textPosition: QRTextPosition,
): QRTextOptions | undefined => {
  if (!username || textPosition === 'none') return undefined;
  return {
    text: username.toUpperCase(),
    position: textPosition,
    fontSize: 22,
    fontWeight: QR_USERNAME_FONT_WEIGHT,
    color: '#111111',
    margin: textPosition === 'below-logo' ? -30 : 50,
    fontFamily: QR_CANVAS_FONT_FAMILY,
  };
};

/** 다운로드·미리보기와 동일한 PNG 캔버스 */
export async function buildQRCodeCanvas(
  value: string,
  textOptions?: QRTextOptions,
  isTransparent: boolean = true,
): Promise<HTMLCanvasElement | null> {
  const logoSizeRatio = 0.34;
  const canvas = document.createElement("canvas");
  await QRCode.toCanvas(canvas, value, {
    errorCorrectionLevel: "H",
    scale: 12,
    margin: 4,
    color: { dark: "#111111", light: isTransparent ? "#00000000" : "#FCFBFB" },
  });

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  if (isTransparent) {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      if (r >= 250 && g >= 250 && b >= 250) {
        data[i + 3] = 0;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  let logoY = 0;
  let logoSize = 0;

  const selectedLogoUrl = isTransparent ? logoQrTransparent : logoQr;

  if (selectedLogoUrl) {
    const logo = new Image();
    logo.crossOrigin = "anonymous";

    await new Promise<void>((resolve, reject) => {
      logo.onload = () => resolve();
      logo.onerror = () => reject(new Error("Failed to load logo image"));
      logo.src = selectedLogoUrl;
    });

    logoSize = canvas.width * logoSizeRatio;
    const logoX = (canvas.width - logoSize) / 2;
    logoY = (canvas.height - logoSize) / 2;

    if (isTransparent) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let y = Math.max(0, Math.floor(logoY)); y < Math.min(canvas.height, Math.ceil(logoY + logoSize)); y++) {
        for (let x = Math.max(0, Math.floor(logoX)); x < Math.min(canvas.width, Math.ceil(logoX + logoSize)); x++) {
          const index = (y * canvas.width + x) * 4;
          data[index + 3] = 0;
        }
      }

      ctx.putImageData(imageData, 0, 0);
    }

    ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);
  }

  if (textOptions?.text) {
    const {
      text,
      position = 'below-qr',
      fontSize = 16,
      color = '#111111',
      margin = 10
    } = textOptions;

    let textY: number;

    if (position === 'below-logo' && selectedLogoUrl) {
      textY = logoY + logoSize + margin;
      await drawQRUsernameOnCanvas(ctx, text, canvas.width / 2, textY, fontSize, color);
    } else if (position === 'below-qr') {
      textY = canvas.height + margin;

      const newHeight = textY + fontSize + margin;
      const newCanvas = document.createElement("canvas");
      newCanvas.width = canvas.width;
      newCanvas.height = newHeight;
      const newCtx = newCanvas.getContext("2d");
      if (newCtx) {
        if (isTransparent) {
          newCtx.drawImage(canvas, 0, 0);
        } else {
          newCtx.fillStyle = "#FCFBFB";
          newCtx.fillRect(0, 0, newCanvas.width, newCanvas.height);
          newCtx.drawImage(canvas, 0, 0);
        }

        await drawQRUsernameOnCanvas(newCtx, text, newCanvas.width / 2, textY, fontSize, color);

        const imageData = newCtx.getImageData(0, 0, newCanvas.width, newCanvas.height);
        canvas.width = newCanvas.width;
        canvas.height = newCanvas.height;
        ctx.putImageData(imageData, 0, 0);
      }
    }
  }

  return canvas;
}

export async function getQRCodeDataUrl(
  value: string,
  textOptions?: QRTextOptions,
  isTransparent: boolean = true,
): Promise<string | null> {
  const canvas = await buildQRCodeCanvas(value, textOptions, isTransparent);
  if (!canvas) return null;
  return canvas.toDataURL("image/png");
}

export async function downloadQRPNG(
  value: string,
  filename = "qr.png",
  textOptions?: QRTextOptions,
  isTransparent: boolean = true
) {
  const canvas = await buildQRCodeCanvas(value, textOptions, isTransparent);
  if (!canvas) return;

  const blob = await new Promise<Blob | null>((res) =>
    canvas.toBlob((b) => res(b), "image/png")
  );

  if (!blob) return;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
